/**
 * useGameRealtime.ts — React binding for the game realtime session (G04).
 *
 * Locked behaviour (TODO.md §7 "Socket.io Contract"):
 *  - initial state comes from HTTP (the database is the source of truth);
 *  - on every contract-valid `game:changed` for this game the full game
 *    state is refetched over HTTP — Socket.io only notifies;
 *  - unmount leaves the room and disconnects the socket.
 *
 * Secret assignments are NEVER fetched by this hook — revealing them is a
 * separate, user-initiated HTTP call (task I07).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientError, type GameChangeReason, type GameDetail } from '@shared';
import { apiV1 } from './api';
import { GameRealtimeSession, type GameRealtimeStatus } from './gameRealtime';
import { createSocket, type SocketFactory } from './socket';

export interface GameRealtimeState {
  /** Authoritative game state from the last successful HTTP fetch. */
  game: GameDetail | null;
  /** True until the initial fetch settles. */
  loading: boolean;
  /** Safe error message when the game could not be loaded (403/404/401). */
  error: string | null;
  /**
   * True while the last BACKGROUND refetch failed and the on-screen state
   * may be stale (K02: a failed refresh is visible, not silent — the last
   * known state stays on screen and the flag clears on the next success).
   */
  stale: boolean;
  /** Live-update channel status (null: no realtime session, e.g. load failed). */
  realtimeStatus: GameRealtimeStatus | null;
  /** Manual refetch of the authoritative state. */
  refetch: () => void;
}

export interface UseGameRealtimeOptions {
  /**
   * Called for every `game:changed` (with the reason) before the refetch
   * starts — e.g. a wishlist modal refetching its own data (task I04/I05).
   */
  onGameChanged?: (reason: GameChangeReason) => void;
  /** Injectable socket factory (tests); defaults to the same-origin production socket. */
  socketFactory?: SocketFactory;
}

export function useGameRealtime(gameId: number, options: UseGameRealtimeOptions = {}): GameRealtimeState {
  const [game, setGame] = useState<GameDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState<GameRealtimeStatus | null>(null);

  // Option identity must never retrigger the effect (parents pass fresh
  // literals each render); only gameId may.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Monotonic generation guard: state from a previous gameId (or a
  // superseded in-flight fetch) must never overwrite the current state.
  const genRef = useRef(0);
  const gameRef = useRef<GameDetail | null>(null);
  const loadRef = useRef<((background: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    const gen = ++genRef.current;
    let cancelled = false;
    let session: GameRealtimeSession | null = null;
    let inFlight = 0;
    let loadedOnce = false;

    const isCurrent = (): boolean => !cancelled && gen === genRef.current;

    setGame(null);
    gameRef.current = null;
    setError(null);
    setStale(false);
    setRealtimeStatus(null);
    setLoading(true);

    const load = async (background: boolean): Promise<void> => {
      const fetchGen = ++inFlight;
      if (!background) setLoading(true);
      try {
        const data = await apiV1.getGame(gameId);
        if (!isCurrent() || fetchGen !== inFlight) return;
        setGame(data);
        gameRef.current = data;
        setError(null);
        setStale(false);
        setLoading(false);
        loadedOnce = true;
      } catch (err) {
        if (!isCurrent() || fetchGen !== inFlight) return;
        if (!loadedOnce) {
          setLoading(false);
          setError(err instanceof ApiClientError ? err.message : 'Could not load this game.');
        } else if (background) {
          // K02: background refetch failure keeps the last known state on
          // screen but is VISIBLE (the page shows a stale note); the next
          // change event or manual refresh retries.
          setStale(true);
        }
      }
    };
    loadRef.current = load;

    void (async () => {
      await load(false);
      if (!isCurrent() || !loadedOnce) return; // no realtime session without read access
      const factory: SocketFactory = optionsRef.current.socketFactory ?? createSocket;
      const realtime = new GameRealtimeSession(factory(), gameId, {
        onGameChanged: (payload) => {
          optionsRef.current.onGameChanged?.(payload.reason);
          void load(true); // authoritative refetch (the contract)
        },
        onStatusChange: (status) => {
          if (isCurrent()) setRealtimeStatus(status);
        },
      });
      session = realtime;
      setRealtimeStatus('connecting');
      try {
        await realtime.connect();
        if (realtime.disposed) return;
        await realtime.subscribe();
      } catch {
        realtime.markUnavailable(); // page still works — HTTP is authoritative
      }
    })();

    return () => {
      cancelled = true;
      loadRef.current = null;
      void session?.dispose();
    };
  }, [gameId]);

  const refetch = useCallback((): void => {
    // Background refresh when a game is already on screen (no blank flash).
    void loadRef.current?.(gameRef.current !== null);
  }, []);

  return { game, loading, error, stale, realtimeStatus, refetch };
}
